import type { DataTableColumnDef } from 'components/tables/data-table';
import { getFilterOptionsFromClassifier } from 'components/tables/list-table/filterUtil';
import { FilterConfig, MainFilter } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useMemo } from 'react';
import { useLazyGetInvoiceTotalsByPayerCountryReportQuery } from 'store/api/reportingFinance';
import { ClassifierData } from 'types/classifiers';
import { InvoiceTotalsByPayerCountryRow, InvoiceTotalsReportTotals } from 'types/reporting/finance';
import { ReportPage } from 'types/reporting/reportPage';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { periodFilter, periodQuickFilter, quickFiltersFrom, searchSelectors } from '../reportFilters';
import { useReportBaseCurrency } from '../reportCurrency';
import { ReportLayout } from '../ReportLayout';
import { toneLegendOf } from '../ReportToneLegend';
import { ReportRowLimitNote } from '../ReportRowLimitNote';
import { ReportEntry } from '../reportCatalog';
import { invoiceTotalsColumns } from './financeReportColumns';
import { invoiceDateBasisFilter } from './financeReportFilters';

type Row = InvoiceTotalsByPayerCountryRow;
type Totals = InvoiceTotalsReportTotals;

const REPORT_KEY = 'invoice-totals-by-payer-country';
const CLASSIFIER_CODES = ['COUNTRY'];

function buildFilterConfig(countries: ClassifierData | undefined): FilterConfig {
    const filters: MainFilter[] = [
        periodFilter(),
        invoiceDateBasisFilter(),
        {
            type: 'multiselect',
            label: t('reporting.filter.country'),
            selector: 'countryCode',
            options: getFilterOptionsFromClassifier(countries),
        },
    ];
    return {
        hideFilterDividers: true,
        quickFilterChips: true,
        periodQuickFilter: periodQuickFilter({ defaultPeriod: 'current-year' }),
        quickFilters: quickFiltersFrom(filters, { dateBasis: 'reporting.filter.date-basis' }),
        searchBarSelectors: searchSelectors('countryName'),
        filters,
    };
}

function buildColumns(rows: Row[] | undefined, currency: string | null): DataTableColumnDef<Row, Totals>[] {
    return [
        {
            id: 'countryCode',
            accessorKey: 'countryCode',
            header: t('reporting.column.country-code'),
            meta: { fixedPx: 90 },
        },
        {
            id: 'countryName',
            accessorKey: 'countryName',
            header: t('reporting.column.payer-country'),
            meta: { widthWeight: 1.4, minPx: 180 },
        },
        ...invoiceTotalsColumns<Row>(rows, currency),
    ];
}

export function InvoiceTotalsByPayerCountryReport({ report }: { report: ReportEntry }) {
    const [fetchData, { data, isFetching, isError }] = useLazyGetInvoiceTotalsByPayerCountryReportQuery();
    const { classifiers } = useClassifiers({ codes: CLASSIFIER_CODES });
    const countries = classifiers['COUNTRY'];
    const filterConfig = useMemo(() => buildFilterConfig(countries), [countries]);
    const currency = useReportBaseCurrency(data?.totals?.baseCurrency ?? data?.content[0]?.baseCurrency);
    const columns = useMemo(() => buildColumns(data?.content, currency), [data, currency]);
    return (
        <ReportLayout report={report} fillHeight toneLegend={toneLegendOf(columns)} baseCurrency={currency}>
            <ListTableWithFilters<Row, ReportPage<Row, Totals>>
                data={data}
                belowQuickFilters={<ReportRowLimitNote report={report} rowCount={data?.content.length} />}
                isLoading={isFetching}
                isError={isError}
                fetchData={fetchData}
                filterConfig={filterConfig}
                columns={columns}
                columnConfigId={`report-${REPORT_KEY}`}
                enableColumnPicker
                enableColumnReorder
                enableColumnResize
                pluralTextId="reporting.countries"
                singularTextId="reporting.country"
                noEntriesId="reporting.no-rows"
                noEntriesBodyId="reporting.no-rows-body"
            />
        </ReportLayout>
    );
}
